import React, { Component } from "react";
import { graphql } from "@apollo/react-hoc";
import gql from "graphql-tag";
import tinyColor from "tinycolor2";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import IconButton from "@mui/material/IconButton";
import Clear from "@mui/icons-material/Clear";
import KeyboardArrowLeft from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRight from "@mui/icons-material/KeyboardArrowRight";
import SwipeableViews from "react-swipeable-views";
import { GenericDialog } from "@igloocloud/igloosharedui";
import { withTranslation } from "react-i18next";

export default graphql(
  gql`
    mutation Rename($id: ID!, $name: String, $picture: NaturalNumber) {
      updateCollection(id: $id, name: $name, picture: $picture) {
        id
        name
        picture
      }
    }
  `,
  {
    name: "Rename",
  }
)(
  withTranslation()(
    class CustomizeCollection extends Component {
      constructor(props) {
        super(props);

        const importAll = (require) =>
          require.keys().reduce((acc, next) => {
            acc[next.replace("./", "")] = require(next);
            return acc;
          }, {});

        this.images = Object.values(
          importAll(
            require.context(
              "../../styles/assets/collectionCards",
              false,
              /\.(png|jpe?g|svg)$/
            )
          )
        );

        this.state = {
          name: "",
          nameEmpty: false,
          nameError: "",
          slideIndex: 0,
        };
      }

      rename = async () => {
        this.setState({ showLoading: true });

        try {
          await this.props.Rename({
            variables: {
              id: this.props.collection.id,
              name: this.state.name,
              picture: this.state.slideIndex,
            },
            optimisticResponse: {
              __typename: "Mutation",
              collection: {
                __typename: this.props.collection.__typename,
                id: this.props.collection.id,
                name: this.state.name,
                picture: this.state.slideIndex,
              },
            },
          });

          this.props.close();
        } catch (e) {
          this.setState({ nameError: "Error" });
        } finally {
          this.setState({ showLoading: false });
        }
      };

      componentDidMount() {
        this.setState({
          slideIndex: this.props.collection.picture,
        });
      }

      componentWillReceiveProps(nextProps) {
        if (this.props.open !== nextProps.open && nextProps.open) {
          this.setState({
            nameEmpty: false,
            name: nextProps.collection.name,
            nameError: "",
          });
        }
      }

      render() {
        const { open, close, t } = this.props;
        const { showLoading } = this.state;
        const {
          REACT_APP_COLLECTION_NAME: collectionName,
          REACT_APP_TEXT_ON_MAIN_BACKGROUND_COLOR: textColor,
        } = process.env;

        return (
          <GenericDialog
            open={open}
            close={close}
            title={t(
              "Customize " + (collectionName?.toLowerCase() || "collection")
            )}
            textButton={t`Close`}
            textButtonFunction={close}
            containedButton={t`Customize`}
            containedButtonFunction={this.rename}
            containedButtonDisabled={
              !this.state.name.replace(/\s/g, "").length ||
              this.state.name.length > 256 ||
              showLoading
            }
            containedButtonLoading={showLoading}
          >
            <TextField
              id="customize-collection-name"
              label={t`Name`}
              value={this.state.name}
              variant="outlined"
              error={
                this.state.nameEmpty ||
                this.state.nameError ||
                this.state.name.length > 256
              }
              helperText={
                this.state.name.length > 256
                  ? t`Use fewer characters`
                  : this.state.nameEmpty
                  ? t`This field is required`
                  : this.state.nameError || " "
              }
              onChange={(event) =>
                this.setState({
                  name: event.target.value,
                  nameEmpty: event.target.value.replace(/\s/g, "") === "",
                  nameError: "",
                })
              }
              onKeyPress={(event) => {
                if (
                  event.key === "Enter" &&
                  this.state.name &&
                  this.state.name.length <= 256
                )
                  this.rename();
              }}
              style={{
                width: "100%",
                marginBottom: "16px",
              }}
              InputLabelProps={this.state.name ? { shrink: true } : {}}
              InputProps={{
                endAdornment: this.state.name && (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() =>
                        this.setState({
                          name: "",
                          nameEmpty: true,
                          nameError: "",
                        })
                      }
                      tabIndex="-1"
                      size="large"
                    >
                      <Clear />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <SwipeableViews
              index={this.state.slideIndex % this.images.length}
              onChangeIndex={(value) => {
                this.setState({
                  slideIndex: value,
                });
              }}
              style={{
                width: "100%",
              }}
            >
              {this.images.map(({ default: image }, index) => (
                <img
                  src={image}
                  key={"collection-image-" + index}
                  alt="Collection"
                  className="notSelectable"
                  draggable="false"
                  style={{
                    width: "100%",
                  }}
                />
              ))}
            </SwipeableViews>
            <div>
              <Button
                size="small"
                onClick={() =>
                  this.setState((oldState) => ({
                    slideIndex: oldState.slideIndex - 1,
                  }))
                }
                disabled={this.state.slideIndex === 0}
                style={{
                  color: tinyColor(textColor)
                    .setAlpha(this.state.slideIndex === 0 ? 0.54 : 1)
                    .toRgbString(),
                }}
              >
                <KeyboardArrowLeft />
                {t`Back`}
              </Button>
              <Button
                size="small"
                onClick={() =>
                  this.setState((oldState) => ({
                    slideIndex: oldState.slideIndex + 1,
                  }))
                }
                disabled={this.state.slideIndex === this.images.length - 1}
                style={{
                  float: "right",
                  marginRight: "0",
                  marginLeft: "auto",
                  color: tinyColor(textColor)
                    .setAlpha(
                      this.state.slideIndex === this.images.length - 1
                        ? 0.54
                        : 1
                    )
                    .toRgbString(),
                }}
              >
                {t`Next`}
                <KeyboardArrowRight />
              </Button>
            </div>
          </GenericDialog>
        );
      }
    }
  )
);
